<template>
  <div id="dashboard" class="container-fluid px-0">
    <section id="seccionCabecera" class="w-100">
      <p class="ps-lg-4 mb-4">
        <span class="fas fa-search fa-lg icon-hover-grow pt-2"></span>
        <span id="cabeceraDashboard" class="text-dark h3 ps-2">{{ $t('incidenceSearchTitle') }}</span>
      </p>
    </section>

    <section class="row mb-3">
      <div class="col-12">
        <div class="card shadow-sm border-0">
          <div class="card-body">
            <form class="row g-3 align-items-end" @submit.prevent="buscarIncidencias(0)">
              <div class="col-12 col-lg-4">
                <label class="form-label fw-semibold">INC</label>
                <input v-model.trim="incId" type="text" class="form-control" placeholder="INC-... / OPIT-..."
                  maxlength="120" />
              </div>

              <div class="col-12 col-lg-4">
                <label class="form-label fw-semibold">Oceane</label>
                <input v-model.trim="oceaneId" type="text" class="form-control" placeholder="OCEANE-..."
                  maxlength="120" />
              </div>

              <!-- <div v-if="canSearchErrors" class="col-12 col-lg-2">
                <label class="form-label fw-semibold d-block">{{ $t('includeErrors') }}</label>
                <div class="form-check form-switch mt-1">
                  <input id="includeErrorsSwitch" v-model="includeErrors" class="form-check-input" type="checkbox" />
                  <label class="form-check-label" for="includeErrorsSwitch">
                    {{ includeErrors ? 'ON' : 'OFF' }}
                  </label>
                </div>
              </div> -->

              <div class="col-12 col-lg-2 d-grid d-lg-flex justify-content-lg-end">
                <button type="submit" class="btn btn-primary px-4" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                  {{ $t('searchButton') }}
                </button>
              </div>
            </form>

            <div v-if="validationError" class="alert alert-warning mt-3 mb-0">{{ validationError }}</div>
            <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">{{ errorMessage }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="row">
      <div class="col-12">
        <div v-if="loading" class="text-muted py-3">{{ $t('loadingSearch') }}</div>

        <div v-else class="buscador-results-table">
          <table id="no-more-tables-search" class="table table-striped table-hover text-dark">
            <thead>
              <tr>
                <th>{{ $t('colIncId') }}</th>
                <th>{{ $t('colOceaneId') }}</th>
                <th>{{ $t('colType') }}</th>
                <th>{{ $t('colTicketDate') }}</th>
                <th>{{ $t('colChannel') }}</th>
                <th>{{ $t('colMessageDate') }}</th>
                <th v-if="canSearchErrors">{{ $t('colErrorCount') }}</th>
                <th>{{ $t('colActions') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in items" :key="groupKey(item)">
                <td>{{ safeText(item.ticket?.ticket_id_inc) }}</td>
                <td>{{ safeText(item.ticket?.ticket_id_oceane) }}</td>
                <td>{{ safeText(item.ticket?.message_type || item.ticket?.message_type_normalized) }}</td>
                <td>{{ formatDate(item.ticket?.created_at) }}</td>
                <td>{{ formatChannel(item.message?.notification_type) }}</td>
                <td>{{ formatDate(item.message?.received_at || item.message?.created_at) }}</td>
                <td v-if="canSearchErrors">
                  <span class="badge" :class="item.error_count > 0 ? 'bg-warning text-dark' : 'bg-secondary'">
                    {{ item.error_count || 0 }}
                  </span>
                </td>
                <td>
                  <button class="btn btn-sm btn-outline-primary" @click="verDetalle(item)">
                    {{ $t('viewDetail') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && items.length === 0 && !errorMessage" class="text-muted py-3">
            {{ $t('noSearchResults') }}
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="btn btn-outline-secondary" @click="buscarIncidencias(Math.max(0, offset - limit))"
            :disabled="loading || offset === 0">
            {{ $t('prevPage') }}
          </button>
          <button class="btn btn-outline-secondary" @click="buscarIncidencias(offset + limit)"
            :disabled="loading || !hasNextPage">
            {{ $t('nextPage') }}
          </button>
        </div>
      </div>
    </section>

    <BModal v-model="showDetailModal" :title="$t('detailTitle')" size="xl" scrollable fullscreen="lg"
      dialog-class="detail-modal-dialog" hide-footer>
      <div v-if="detailLoading" class="text-muted py-3">
        {{ $t('loadingSearch') }}
      </div>

      <div v-else-if="detailError" class="alert alert-danger mb-0">
        {{ detailError }}
      </div>

      <div v-else-if="selectedItem" class="detail-layout">
        <section class="detail-section mb-3">
          <h6 class="fw-bold text-dark mb-3">{{ $t('latestCommunication') }}</h6>
          <div class="history-block">
            <div class="detail-kv-table" role="table">
              <div
                v-for="row in communicationFieldRows(selectedItem)"
                :key="`latest-${row.key}`"
                class="detail-kv-row"
                role="row"
              >
                <div class="detail-kv-label" role="rowheader">{{ row.label }}</div>
                <div class="detail-kv-value" role="cell">
                  <pre v-if="row.isPre" class="detail-pre m-0">{{ row.value }}</pre>
                  <span v-else>{{ row.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="detail-section mb-3">
          <h6 class="fw-bold text-dark mb-3">{{ $t('communicationHistory') }}</h6>

          <div v-if="historyItems.length === 0" class="detail-empty">
            {{ $t('noCommunicationHistory') }}
          </div>

          <div v-else class="history-list">
            <article v-for="historyItem in historyItems" :key="historyItem.ticket?.id" class="history-block">
              <div class="detail-kv-table" role="table">
                <div
                  v-for="row in communicationFieldRows(historyItem, { includeMessage: false })"
                  :key="`hist-${historyItem.ticket?.id}-${row.key}`"
                  class="detail-kv-row"
                  role="row"
                >
                  <div class="detail-kv-label" role="rowheader">{{ row.label }}</div>
                  <div class="detail-kv-value" role="cell">{{ row.value }}</div>
                </div>
              </div>

              <button v-if="canViewOriginalMessage" type="button" class="btn btn-sm btn-link px-0"
                @click="toggleHistoryMessage(historyItem.ticket?.id)">
                {{ isHistoryMessageExpanded(historyItem.ticket?.id) ? $t('hideMessage') : $t('showMessage') }}
              </button>

              <pre v-if="canViewOriginalMessage && isHistoryMessageExpanded(historyItem.ticket?.id)"
                class="detail-pre mt-2 mb-0">{{ detailValue(historyItem.message?.text) }}</pre>
            </article>
          </div>
        </section>

        <section v-if="canSearchErrors" class="detail-section">
          <h6 class="fw-bold text-dark mb-3">{{ $t('errorsBlock') }}</h6>

          <div v-if="!selectedItem.errors || selectedItem.errors.length === 0" class="detail-empty">
            {{ $t('noRelatedErrors') }}
          </div>

          <div v-else class="history-list">
            <article v-for="error in selectedItem.errors" :key="error.id" class="history-block">
              <div class="detail-kv-table" role="table">
                <div class="detail-kv-row" role="row">
                  <div class="detail-kv-label" role="rowheader">{{ $t('fieldErrorService') }}</div>
                  <div class="detail-kv-value" role="cell">{{ formatErrorService(error.service) }}</div>
                </div>
                <div class="detail-kv-row" role="row">
                  <div class="detail-kv-label" role="rowheader">{{ $t('fieldErrorDate') }}</div>
                  <div class="detail-kv-value" role="cell">{{ detailValue(error.created_at) }}</div>
                </div>
                <div class="detail-kv-row" role="row">
                  <div class="detail-kv-label" role="rowheader">{{ $t('fieldErrorReason') }}</div>
                  <div class="detail-kv-value" role="cell">{{ detailValue(error.reason) }}</div>
                </div>
                <div class="detail-kv-row" role="row">
                  <div class="detail-kv-label" role="rowheader">{{ $t('fieldErrorMessage') }}</div>
                  <div class="detail-kv-value" role="cell">{{ detailValue(error.error_message) }}</div>
                </div>
                <div v-if="getMissingFields(error).length > 0" class="detail-kv-row" role="row">
                  <div class="detail-kv-label" role="rowheader">{{ $t('fieldMissingFields') }}</div>
                  <div class="detail-kv-value" role="cell">{{ getMissingFields(error).join(', ') }}</div>
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>
    </BModal>
  </div>
</template>

<script>
import auth from '../auth.js';
import { BModal } from 'bootstrap-vue-next';

export default {
  name: 'BuscadorIncidencias',
  components: { BModal },
  data() {
    return {
      incId: '',
      oceaneId: '',
      includeErrors: false,
      loading: false,
      validationError: '',
      errorMessage: '',
      items: [],
      total: 0,
      limit: 20,
      offset: 0,
      padmin: false,
      peditor: false,
      pconsultorErrores: false,
      showDetailModal: false,
      selectedItem: null,
      relatedItems: [],
      detailLoading: false,
      detailError: '',
      expandedHistoryIds: [],
    };
  },
  computed: {
    canSearchErrors() {
      return Boolean(this.padmin || this.peditor);
    },
    canViewOriginalMessage() {
      return Boolean(this.padmin || this.peditor || this.pconsultorErrores);
    },
    hasNextPage() {
      return this.offset + this.limit < this.total;
    },
    historyItems() {
      return this.relatedItems.slice(1);
    },
  },
  async created() {
    this.$store.state.botonEditar = false;
    await this.consultarPermisos();
  },
  methods: {
    groupKey(item) {
      const source = item?.source ?? 'etlred';
      const inc = item?.ticket?.ticket_id_inc ?? '';
      const oceane = item?.ticket?.ticket_id_oceane ?? '';
      return `${source}::${inc}::${oceane}`;
    },
    formatDate(value) {
      if (!value) return 'N/A';
      const date = new Date(value);
      if (isNaN(date.getTime())) return 'N/A';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    },
    safeText(value) {
      return value == null || value === '' ? 'N/A' : value;
    },
    detailValue(value) {
      if (value === null || value === undefined || value === '') return 'No disponible';
      return value;
    },
    formatChannel(value) {
      const normalized = String(value || '').trim().toLowerCase();
      if (normalized === 'it') return 'IT';
      if (normalized.includes('emerg')) return 'Emergencia';
      if (normalized.includes('crit')) return 'Crítica';
      return this.safeText(value);
    },
    formatErrorService(service) {
      const normalized = String(service || '').trim().toLowerCase();
      if (normalized === 'telegram') return 'Telegram';
      if (normalized === 'endpoint' || normalized === 'process message') return 'API';
      return this.detailValue(service);
    },
    detailIncidentNumber(ticket) {
      if (!ticket) return 'No disponible';
      const inc = ticket.ticket_id_inc || 'No disponible';
      const oceane = ticket.ticket_id_oceane || 'No disponible';
      return `${inc} / ${oceane}`;
    },
    communicationFieldRows(item, { includeMessage = true } = {}) {
      const ticket = item?.ticket || {};
      const message = item?.message || {};
      const rows = [
        { key: 'incident_ref', label: this.$t('fieldId'), value: this.detailIncidentNumber(ticket) },
        { key: 'type', label: this.$t('fieldType'), value: this.detailValue(ticket.message_type || ticket.message_type_normalized) },
        { key: 'channel', label: this.$t('fieldChannel'), value: this.formatChannel(message.notification_type) },
        { key: 'ticket_date', label: this.$t('fieldTicketDate'), value: this.formatDate(ticket.created_at) },
        { key: 'message_date', label: this.$t('fieldMessageDate'), value: this.formatDate(message.received_at || message.created_at) },
        { key: 'title', label: this.$t('fieldTitle'), value: this.detailValue(ticket.title) },
        { key: 'scale_level', label: this.$t('fieldScaleLevel'), value: this.detailValue(ticket.scale_level) },
        { key: 'affected_service', label: this.$t('fieldAffectedService'), value: this.detailValue(ticket.affected_service) },
        { key: 'affected_equipment', label: this.$t('fieldAffectedEquipment'), value: this.detailValue(ticket.affected_equipment) },
        { key: 'impact', label: this.$t('fieldImpact'), value: this.detailValue(ticket.impact) },
        { key: 'IM', label: this.$t('fieldResponsible'), value: this.detailValue(ticket.IM) },
        { key: 'actions', label: this.$t('fieldActions'), value: this.detailValue(ticket.actions) },
      ];

      if (includeMessage && this.canViewOriginalMessage) {
        rows.push({
          key: 'message',
          label: this.$t('fieldOriginalMessage'),
          value: this.detailValue(message.text),
          isPre: true,
        });
      }

      return rows;
    },
    getMissingFields(error) {
      const fields = error?.extra?.fields;
      return Array.isArray(fields) ? fields.filter(Boolean) : [];
    },
    toggleHistoryMessage(ticketId) {
      const id = this.normalizeHistoryTicketId(ticketId);
      if (id == null) return;

      if (this.expandedHistoryIds.includes(id)) {
        this.expandedHistoryIds = this.expandedHistoryIds.filter(itemId => itemId !== id);
      } else {
        this.expandedHistoryIds = [...this.expandedHistoryIds, id];
      }
    },
    isHistoryMessageExpanded(ticketId) {
      const id = this.normalizeHistoryTicketId(ticketId);
      return id != null && this.expandedHistoryIds.includes(id);
    },
    normalizeHistoryTicketId(ticketId) {
      if (ticketId == null || ticketId === '') return null;
      const asString = String(ticketId);
      if (asString.startsWith('it:')) return asString;

      const numericId = Number(ticketId);
      return Number.isFinite(numericId) ? numericId : asString;
    },
    verDetalle(item) {
      this.showDetailModal = true;
      this.selectedItem = null;
      this.relatedItems = [];
      this.detailError = '';
      this.detailLoading = true;
      this.expandedHistoryIds = [];
      this.cargarDetalle(item);
    },
    async consultarPermisos() {
      try {
        const response = await fetch('api/permisos', {
          method: 'GET',
          headers: auth.getAuthHeader()
        });

        const data = await response.json();
        this.peditor = Boolean(data?.[0]?.editor === 1);
        this.padmin = Boolean(data?.[0]?.admin === 1);
        this.pconsultorErrores = Boolean(data?.[0]?.consultor_errores === 1);

        if (!this.canSearchErrors) {
          this.includeErrors = false;
        }
      } catch (error) {
        this.peditor = false;
        this.padmin = false;
        this.pconsultorErrores = false;
        this.includeErrors = false;
      }
    },
    async buscarIncidencias(nextOffset = 0) {
      this.validationError = '';
      this.errorMessage = '';

      if ((!this.incId || !this.incId.trim()) && (!this.oceaneId || !this.oceaneId.trim())) {
        this.validationError = 'Debes informar un INC, OPIT u Oceane para buscar.';
        return;
      }

      this.loading = true;
      this.offset = Math.max(0, Number(nextOffset) || 0);

      try {
        const payload = {
          incId: this.incId.trim(),
          oceaneId: this.oceaneId.trim(),
          limit: this.limit,
          offset: this.offset,
          includeErrors: this.canSearchErrors ? this.includeErrors : false,
        };

        const response = await fetch('api/etlred/incidencias/buscar', {
          method: 'POST',
          headers: {
            ...auth.getAuthHeader()
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (!response.ok) {
          this.errorMessage = result?.message || this.$t('searchError');
          this.items = [];
          this.total = 0;
          return;
        }

        this.items = Array.isArray(result?.items) ? result.items : [];
        this.total = Number(result?.total || 0);
      } catch (error) {
        this.errorMessage = this.$t('searchError');
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async cargarDetalle(item) {
      try {
        const response = await fetch('api/etlred/incidencias/detalle', {
          method: 'POST',
          headers: {
            ...auth.getAuthHeader()
          },
          body: JSON.stringify({
            ticketDbId: item?.ticket?.id,
            source: item?.source ?? 'etlred',
            ttNumber: item?.source === 'it_alarm' ? item?.ticket?.ticket_id_inc : undefined,
            includeErrors: this.canSearchErrors ? this.includeErrors : false,
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || this.$t('searchError'));
        }

        this.relatedItems = Array.isArray(result.items) ? result.items : [];
        this.selectedItem = result.item
          ? { ...result.item, errors: Array.isArray(result.errors) ? result.errors : (result.item.errors || []) }
          : null;
      } catch (error) {
        this.detailError = error?.message || this.$t('searchError');
        this.selectedItem = null;
        this.relatedItems = [];
      } finally {
        this.detailLoading = false;
      }
    }
  }
};
</script>

<style scoped>
:deep(.detail-modal-dialog) {
  display: flex;
  align-items: center;
  width: min(96vw, 1440px);
  max-width: min(96vw, 1440px);
  min-height: calc(100% - 2rem);
  margin: 1rem auto;
}

:deep(.detail-modal-dialog .modal-content) {
  height: 80vh;
}

.detail-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-section {
  padding: 1rem;
  border: 1px solid #e5ebf2;
  border-radius: 8px;
  background: #ffffff;
}

.detail-kv-table {
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.detail-kv-row {
  display: grid;
  grid-template-columns: 35% 65%;
  border-bottom: 1px solid #dee2e6;
}

.detail-kv-row:last-child {
  border-bottom: none;
}

.detail-kv-label {
  background: #f8fafc;
  font-weight: 600;
  vertical-align: top;
  padding: 0.5rem 0.75rem;
  border-right: 1px solid #dee2e6;
}

.detail-kv-value {
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0.5rem 0.75rem;
  background: #ffffff;
}

.detail-pre {
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
  color: #243443;
}

.detail-empty {
  padding: 0.95rem 1rem;
  border-radius: 8px;
  border: 1px dashed #d6dde6;
  color: #6c757d;
  background: #fcfdff;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.history-block {
  padding: 0.85rem;
  border: 1px solid #e8edf3;
  border-radius: 8px;
  background: #fbfcfd;
}

@media only screen and (max-width: 1200px) {

  .buscador-results-table table,
  .buscador-results-table thead,
  .buscador-results-table tbody,
  .buscador-results-table th,
  .buscador-results-table td,
  .buscador-results-table tr {
    display: block;
  }

  .buscador-results-table thead tr {
    position: absolute;
    top: -9999px;
    left: -9999px;
  }

  .buscador-results-table tr {
    border: 1px solid #ccc;
    margin-bottom: 8px;
  }

  .buscador-results-table td {
    border: none;
    border-bottom: 1px solid #eee;
    position: relative;
    padding-left: 50%;
    white-space: normal;
    text-align: left;
  }

  .buscador-results-table td:before {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 45%;
    padding-right: 8px;
    white-space: nowrap;
    font-weight: 600;
  }

  .buscador-results-table td:nth-of-type(1):before {
    content: 'ID INC';
  }

  .buscador-results-table td:nth-of-type(2):before {
    content: 'ID OCEANE';
  }

  .buscador-results-table td:nth-of-type(3):before {
    content: 'Tipo';
  }

  .buscador-results-table td:nth-of-type(4):before {
    content: 'Fecha ticket';
  }

  .buscador-results-table td:nth-of-type(5):before {
    content: 'Canal';
  }

  .buscador-results-table td:nth-of-type(6):before {
    content: 'Fecha mensaje';
  }

  .buscador-results-table td:nth-of-type(7):before {
    content: 'Errores';
  }

  .buscador-results-table td:nth-of-type(8):before {
    content: 'Acciones';
  }
}
</style>
